import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Upload, Loader2, X, Image as ImageIcon } from 'lucide-react';
import { API, adminAuthHeader, resolveImg } from '../api';

/**
 * Reusable image upload component.
 * Props:
 *  - value: current image URL string
 *  - onChange: (newUrl: string) => void
 *  - label: label text
 *  - hint: helper text
 *  - height: preview height (default h-40)
 */
const ImageUploader = ({ value, onChange, label = 'Image', hint = 'JPG, PNG or WEBP up to 8 MB', height = 'h-40' }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');

  const upload = async (file) => {
    if (!file) return;
    setErr('');
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/admin/upload`, form, {
        headers: { ...adminAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });
      // Store the PATH-RELATIVE url so PDFs & previews stay portable across
      // preview URL rotations. The display side prepends REACT_APP_BACKEND_URL
      // via resolveImg().
      const url = res.data.url; // e.g. /api/uploads/<file>
      onChange(url);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  return (
    <div>
      {label && <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>}

      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`relative ${height} border-2 border-dashed rounded-md flex items-center justify-center cursor-pointer transition ${
          dragging ? 'border-[#0d64a8] bg-[#e8f2fb]' : 'border-gray-300 bg-gray-50 hover:border-[#0d64a8] hover:bg-white'
        }`}
        onClick={() => !uploading && inputRef.current?.click()}
      >
        {value ? (
          <>
            <img
              key={value}
              src={resolveImg(value)}
              alt="preview"
              className="max-h-full max-w-full object-contain rounded"
              onError={(e) => {
                // Show a visible fallback instead of hiding — the previous
                // display:none behaviour made the panel look empty and confused
                // admins when a stored external URL later went dead.
                e.currentTarget.style.visibility = 'hidden';
                const fb = e.currentTarget.nextElementSibling;
                if (fb && fb.dataset && fb.dataset.role === 'img-fallback') fb.style.display = 'flex';
              }}
              onLoad={(e) => {
                e.currentTarget.style.visibility = 'visible';
                const fb = e.currentTarget.nextElementSibling;
                if (fb && fb.dataset && fb.dataset.role === 'img-fallback') fb.style.display = 'none';
              }}
            />
            <div
              data-role="img-fallback"
              className="absolute inset-1 rounded bg-amber-50 border border-amber-200 text-amber-800 text-[11px] flex-col items-center justify-center gap-1 p-2 text-center"
              style={{ display: 'none' }}
            >
              <span className="font-semibold">Image unavailable</span>
              <span className="opacity-80 leading-tight">The stored URL didn't load. Re-upload or paste a new URL.</span>
            </div>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onChange(''); }}
              className="absolute top-1 right-1 w-6 h-6 bg-white/90 border border-gray-200 rounded-full flex items-center justify-center text-gray-600 hover:text-red-500"
              title="Remove"
            >
              <X size={13} />
            </button>
          </>
        ) : uploading ? (
          <div className="flex items-center gap-2 text-sm text-[#0d64a8]">
            <Loader2 size={16} className="animate-spin" /> Uploading...
          </div>
        ) : (
          <div className="text-center px-4">
            <div className="w-9 h-9 rounded-full bg-[#0d64a8]/10 text-[#0d64a8] flex items-center justify-center mx-auto mb-1">
              <Upload size={16} />
            </div>
            <div className="text-sm text-gray-700 font-medium">Drop an image or click to upload</div>
            <div className="text-[11px] text-gray-500 mt-0.5">{hint}</div>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
        />
      </div>

      <div className="mt-2 flex items-center gap-2 text-xs">
        <ImageIcon size={12} className="text-gray-400 shrink-0" />
        <input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="or paste an image URL"
          className="flex-1 border border-gray-200 rounded px-2 py-1 outline-none focus:border-[#0d64a8]"
        />
      </div>
      {err && <div className="mt-1 text-xs text-red-600">{err}</div>}
    </div>
  );
};

export default ImageUploader;
